import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';

import AuthLayout from '../../../layouts/AuthLayout/AuthLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { useAuth } from '../../../context/AuthProvider/AuthProvider';

import { NioButton, NioToaster, NioCard, NioBrand, NioField } from '../../../components';
import AuthLogo from '../../../components/AuthLogo/AuthLogo';
import { apiRegister } from '../../../api/auth';

const LOGIN_PATH = '/auth/login';

export default function Signup() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data) => {
    clearErrors('email');
    try {
      const name = data.name.trim();
      const email = data.email.trim();
      const res = await apiRegister({ name, email, password: data.password });
      login({ token: res.token, user: res.user });
      reset();
      navigate(LOGIN_PATH, {
        replace: true,
        state: { signupSuccess: true, email },
      });
    } catch (err) {
      const msg = err?.message === 'email_taken'
        ? t('auth.signup.err.emailTaken')
        : (err?.message || t('auth.signup.err.generic'));
      setError('root', { type: 'manual', message: msg });
    }
  };

  useEffect(() => {
    if (errors.email && errors.password) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  }, [errors.email, errors.password]);

  return (
    <AuthLayout titleKey="auth.signup.docTitle" rootClass="layout-1">
      {showError && (
        <NioToaster
          variant="error"
          messages={[{ id: 2, header: t('auth.signup.toastErrTitle'), body: t('auth.signup.toastErrBody') }]}
        />
      )}

      <div className="jassp-auth-panel px-2">
        <NioCard className="jassp-auth-card border-0 shadow-none">
          <NioCard.Body className="p-0">
            <div className="jassp-auth-logo mb-4">
              <AuthLogo />
            </div>

            <h1 className="jassp-auth-title">{t('auth.signup.title')}</h1>

            {errors.root?.message ? (
              <p className="text-danger small mb-3" role="alert">
                {errors.root.message}
              </p>
            ) : null}

            <Form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Row className="gy-3">
                <Col xs={12}>
                  <NioField className="jassp-auth-field" label={t('auth.signup.name')} htmlFor="fname">
                    <NioField.Input
                      id="fname"
                      placeholder=""
                      errors={errors.name}
                      {...register('name', { required: t('auth.signup.err.required') })}
                    />
                  </NioField>
                </Col>
                <Col xs={12}>
                  <NioField className="jassp-auth-field" label={t('auth.signup.email')} htmlFor="email">
                    <NioField.Input
                      type="email"
                      id="email"
                      placeholder=""
                      errors={errors.email}
                      {...register('email', {
                        required: t('auth.signup.err.required'),
                        pattern: {
                          value:
                            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                          message: t('auth.signup.err.invalidEmail'),
                        },
                      })}
                    />
                  </NioField>
                </Col>
                <Col xs={12}>
                  <NioField className="jassp-auth-field" htmlFor="password" label={t('auth.signup.password')}>
                    <NioField.Input
                      id="password"
                      type="password"
                      icon="eye-fill after text-primary"
                      placeholder=""
                      {...register('password', {
                        required: t('auth.signup.err.required'),
                      })}
                    />
                  </NioField>
                </Col>
              </Row>

              <p className="jassp-auth-recaptcha-note mb-0">
                {t('auth.signup.recaptchaLead')}{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  {t('auth.signup.recaptchaPrivacy')}
                </a>{' '}
                {t('auth.signup.recaptchaAnd')}{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  {t('auth.signup.recaptchaTerms')}
                </a>{' '}
                {t('auth.signup.recaptchaApply')}
              </p>

              <NioButton
                type="submit"
                label={t('auth.signup.submit')}
                className="jassp-auth-submit mt-4 w-100 border-0"
              />
            </Form>

            <p className="text-center mt-4 mb-0 small text-secondary">
              {t('auth.signup.hasAccount')}{' '}
              <Link to="/auth/login" className="jassp-auth-link fw-semibold">
                {t('auth.signup.loginLink')}
              </Link>
            </p>
          </NioCard.Body>
        </NioCard>
      </div>
    </AuthLayout>
  );
}
